import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Generate or get session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('analytics_session_id');
  if (!sessionId) {
    sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
    sessionStorage.setItem('analytics_session_id', sessionId);
  }
  return sessionId;
};

// Detect device type
const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return 'tablet';
  }
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua)) {
    return 'mobile';
  }
  return 'desktop';
};

export const AnalyticsTracker = () => {
  const location = useLocation();
  const startTimeRef = useRef(Date.now());
  const currentPageRef = useRef(location.pathname);
  const sessionId = useRef(getSessionId());

  // Record visit when page changes
  useEffect(() => {
    const recordVisit = async () => {
      try {
        await axios.post(`${API_BASE}/api/analytics/visit`, {
          session_id: sessionId.current,
          page: location.pathname,
          referrer: document.referrer || null,
          user_agent: navigator.userAgent,
          device_type: getDeviceType(),
          duration: 0
        });
      } catch (error) {
        // Silently fail - don't break the app for analytics
        console.debug('Analytics: Failed to record visit');
      }
    };

    // Update duration for previous page before recording new visit
    const updatePreviousPageDuration = async () => {
      if (currentPageRef.current !== location.pathname) {
        const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
        try {
          await axios.put(`${API_BASE}/api/analytics/visit`, {
            session_id: sessionId.current,
            page: currentPageRef.current,
            duration: duration
          });
        } catch (error) {
          console.debug('Analytics: Failed to update duration');
        }
      }
    };

    updatePreviousPageDuration().then(() => {
      recordVisit();
      startTimeRef.current = Date.now();
      currentPageRef.current = location.pathname;
    });
  }, [location.pathname]);

  // Update duration when user leaves the page
  useEffect(() => {
    const handleBeforeUnload = () => {
      const duration = Math.round((Date.now() - startTimeRef.current) / 1000);
      // Use sendBeacon for reliable delivery on page unload
      const data = JSON.stringify({
        session_id: sessionId.current,
        page: currentPageRef.current,
        duration: duration
      });
      navigator.sendBeacon('/api/analytics/visit', new Blob([data], { type: 'application/json' }));
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        handleBeforeUnload();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  return null; // This component doesn't render anything
};
